import React, { useState } from 'react'
import Layout from '../components/Layout'
import Table from '../components/common/Table'
import Button from '../components/common/Button'
import ContractInvoiceItem from '../components/ContractInvoiceItem'
import ContractMonthOpen from '../components/ContractMonthOpen'
import ContractTabs from '../components/ContractTabs'
import { useModal } from '../hooks/modal'
import { useLoad } from '../hooks/request'
import { BILLING_INVOICE_LIST, BILLING_PERIOD_LIST, BILLING_SUMMARY } from '../urls'
import { checkPermission as cp } from '../utils/auth'
import { PermissionDenied } from '../components/PermissionDenied'

function money(value) {
    return new Intl.NumberFormat('ru-RU').format(value || 0)
}

function monthOf(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Tanlash uchun oylar: kelajak oylar ham bor — predoplata (masalan oktyabr)
 * uchun oy ochib qo'yish kerak bo'ladi.
 */
function monthOptions(future = 3, past = 12) {
    const now = new Date()
    const items = []
    for (let i = future; i >= -past; i -= 1) {
        items.push(monthOf(new Date(now.getFullYear(), now.getMonth() + i, 1)))
    }
    return items
}

export default function ContractPayments() {
    const months = monthOptions()
    const [period, setPeriod] = useState(monthOf(new Date()))
    const [status, setStatus] = useState('')
    const [module, setModule] = useState('')
    const [search, setSearch] = useState('')
    const [notice, setNotice] = useState(null)

    const params = {
        period,
        ...(status ? { status } : {}),
        ...(module ? { module } : {}),
        ...(search ? { search } : {}),
    }

    const invoices = useLoad({ url: BILLING_INVOICE_LIST, params }, [period, status, module, search])
    const summary = useLoad({ url: BILLING_SUMMARY, params: { period, ...(module ? { module } : {}) } },
        [period, module])
    const periods = useLoad({ url: BILLING_PERIOD_LIST }, [])

    // Backend `YYYY-MM-DD` qaytaradi — tanlovdagi `YYYY-MM` bilan solishtiramiz.
    const openMonths = (periods.response ? periods.response.results : []).map((m) => String(m).slice(0, 7))
    const isOpen = openMonths.includes(period)

    function reload() {
        invoices.request()
        summary.request()
        periods.request()
    }

    const [showOpenModal, hideOpenModal] = useModal(
        <ContractMonthOpen
            period={period}
            periods={openMonths}
            onCancel={() => hideOpenModal()}
            onSuccess={(response, openedPeriod) => {
                hideOpenModal()
                const created = response ? response.created : 0
                const existing = response ? response.existing : 0
                const updated = response ? response.updated : 0

                if (!created && !existing && !updated) {
                    setNotice({ type: 'is-warning', text: 'Подходящих компаний за этот месяц не найдено' })
                } else {
                    const parts = []
                    if (created) parts.push(`добавлено ${created}`)
                    if (updated) parts.push(`обновлено сумм: ${updated}`)
                    if (existing) parts.push(`уже было ${existing}`)
                    setNotice({ type: 'is-success', text: `${openedPeriod}: ${parts.join(', ')}` })
                }

                if (openedPeriod && openedPeriod !== period) setPeriod(openedPeriod)
                reload()
            }} />,
    )

    if (!cp('billing.view_contractinvoice')) {
        return <PermissionDenied />
    }

    const s = summary.response || {}

    return (
        <Layout>
            <span className="is-size-4">Платежи по договорам</span>

            {cp('billing.add_contractinvoice') ? (
                <Button
                    onClick={showOpenModal}
                    text="Открыть месяц"
                    icon="ion-md-add"
                    className="is-pulled-right is-link is-outlined" />
            ) : null}

            <br /><br />

            <ContractTabs />

            {notice ? (
                <div className={`notification ${notice.type} is-light`}>
                    <button type="button" className="delete" onClick={() => setNotice(null)} />
                    {notice.text}
                </div>
            ) : null}

            <div>
                <div className="columns">
                    <div className="column">
                        <div className="box">
                            <p className="heading">Ожидается</p>
                            <p className="title is-5">{money(s.expectedTotal)}</p>
                        </div>
                    </div>
                    <div className="column">
                        <div className="box">
                            <p className="heading">Собрано</p>
                            <p className="title is-5 has-text-success">{money(s.paidTotal)}</p>
                        </div>
                    </div>
                    <div className="column">
                        <div className="box">
                            <p className="heading">Долг</p>
                            <p className="title is-5 has-text-danger">{money(s.debtTotal)}</p>
                        </div>
                    </div>
                    <div className="column">
                        <div className="box">
                            <p className="heading">Оплатили</p>
                            <p className="title is-5">{s.paidCount || 0} / {s.invoiceCount || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="columns">
                    <div className="column is-3">
                        <div className="select is-fullwidth">
                            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
                                {months.map((m) => (
                                    <option key={m} value={m}>
                                        {m}{openMonths.includes(m) ? '' : ' · не открыт'}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="column is-2">
                        <div className="select is-fullwidth">
                            <select value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="">Все статусы</option>
                                <option value="unpaid">Не оплачено</option>
                                <option value="partial">Частично</option>
                                <option value="paid">Оплачено</option>
                            </select>
                        </div>
                    </div>
                    <div className="column is-2">
                        <div className="select is-fullwidth">
                            <select value={module} onChange={(e) => setModule(e.target.value)}>
                                <option value="">Все модули</option>
                                <option value="zero">Nollik</option>
                                <option value="manthly">Oylik</option>
                            </select>
                        </div>
                    </div>
                    <div className="column">
                        <input
                            className="input"
                            placeholder="Поиск по названию или СТИР..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>

                {!isOpen && !periods.loading ? (
                    <div className="notification is-info is-light">
                            Месяц {period} ещё не открыт — строк нет.
                        {cp('billing.add_contractinvoice') ? (
                            <span>
                                    &nbsp;
                                <Button
                                    onClick={showOpenModal}
                                    text="Открыть месяц"
                                    className="is-link is-small" />
                                    &nbsp;— суммы можно скопировать из прошлого месяца.
                            </span>
                        ) : null}
                    </div>
                ) : null}

                <Table
                    loading={invoices.loading}
                    items={invoices.response ? invoices.response.results : []}
                    columns={{
                        company: 'Компания',
                        expected: 'Договор',
                        paid: 'Оплачено',
                        debt: 'Остаток',
                        status: 'Статус',
                        payments: 'Платежи',
                        actions: '',
                    }}
                    renderItem={(item) => (
                        <ContractInvoiceItem
                            key={item.id}
                            item={item}
                            onUpdate={reload} />
                    )} />
            </div>
        </Layout>
    )
}
